/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import {
  composeEntityMergers,
  entityFieldMergeBy,
  entityFieldsMerger,
  entityReplaceOrMergeWith,
  onChangeEntityMerger,
  setDirty,
  setEntityField,
  setEntityFields,
  setNestedEntityField,
  setRecalculationNeeded,
  setUpdatedDirtyType,
} from '@gh/common-shared';
import { EntityVersion, ID } from '@gh/common-shared-data';
import { GridLayout } from '@gh/core-ui-grid';
import { EMPTY_ARRAY } from '@gh/core-util';
import { constant, isEmpty, isEqual, orderBy } from 'lodash';
import {
  getJeComponentId,
  getJeCostCenterId,
  getJeStrId,
  getJeTopId,
  isJeSalesTaxItem,
  JeComponent,
  JeCostCenter,
  JeDetails,
  JeElementType,
  JeItem,
  JeStr,
  JeTop,
} from '../../../resources/job-estimate-details.model';
import {
  getJeBestComponentDetailsTab,
  jeCcTable,
  jeComponentTable,
  JeDetailsState,
  JeDetailsStateEvent,
  JeDetailsStateEventType,
  jeItemTable,
  jeOrderTable,
  jeTopRefTable,
  jeTopTable,
  jeUomRefTable,
  queryJeAllModifiedComponents,
  queryJeAllModifiedCostCenters,
  queryJeIsSelectionEmpty,
  queryJeTops,
  initHistory,
  saveViewToHistory,
  addToHistory,
  undo,
  redo,
} from '../je-details.state';
import {
  jeDetailsCcItemReducer,
  mergeJeItem,
  setCostCenterItemField,
} from './je-details-cc-item.reducers';
import { jeDetailsCcReducer, mergeCostCenter } from './je-details-cc.reducers';
import { jeDetailsComponentReducer, setComponentField } from './je-details-component.reducers';
import { mergeCuttingList } from './je-details-cutting-list.reducers';
import { jeDetailsOrderReducer, mergeJeOrder } from './je-details-order.reducers';
import { selectionReducer } from './je-details-selection.reducers';
import { jeDetailsTopReducer } from './je-details-top.reducers';

const mergeJeTop = entityReplaceOrMergeWith(entityFieldsMerger<JeTop>(['count', 'value']));
const mergeJeCostCenter = entityReplaceOrMergeWith(entityFieldsMerger<JeCostCenter>(['cost']));
const mergeJeComponent = entityReplaceOrMergeWith(composeEntityMergers(
  entityFieldsMerger<JeComponent>(['cost', 'quantity', 'extension', 'description']),
  entityFieldMergeBy<JeComponent, 'cuttingList'>('cuttingList', mergeCuttingList)));
// if UoM has been change we have to reset cutting list and quantity
// this is relevant only for REPRICE operation
const mergeComponentUom = onChangeEntityMerger(
  entityFieldsMerger(['uomId']),
  (component: JeComponent) => isEmpty(component!.cuttingList)
    ? component
    : setComponentField(component, 'cuttingList', EMPTY_ARRAY));
const mergeItemUom = onChangeEntityMerger<JeItem>(
  entityFieldsMerger(['uomId']),
  (item: JeItem) => isEmpty(item!.cuttingList)
    ? item
    : setCostCenterItemField(item, 'cuttingList', EMPTY_ARRAY));

const repriceJeComponent = entityReplaceOrMergeWith(onChangeEntityMerger<any>(
  mergeComponentUom,
  (item) => setUpdatedDirtyType(item!)));
const repriceJeItem = entityReplaceOrMergeWith(onChangeEntityMerger<any>(
  composeEntityMergers(mergeItemUom, entityFieldsMerger<JeItem>(['cost', 'isSalesTax', 'description'])),
  (item) => setUpdatedDirtyType(item!)));

export function jeDetailsReducer(state: JeDetailsState, event: JeDetailsStateEvent): JeDetailsState {
  switch (event.type) {
    case JeDetailsStateEventType.Load:
      return loadDetails(state, event.details, event.layouts, event.hasEditPermission);
    case JeDetailsStateEventType.LoadWithCostCenter:
      if (event.costCenterId && event.costCenter) {
        return mergeCostCenter(loadDetails(state, event.details), <ID>event.costCenterId, event.costCenter);
      } else {
        return loadDetails(state, event.details);
      }
    case JeDetailsStateEventType.MergeOnRecalculate:
      return mergeDetailsOnRecalculate(state, event.details);
    case JeDetailsStateEventType.MergeOnReprice:
      return mergeDetailsOnReprice(state, event.details);
    case JeDetailsStateEventType.SalesTaxRatesSet:
      return updateSalesTaxRates(state, event.rates);
    case JeDetailsStateEventType.Authorize:
      return authorize(state, event.version);
    case JeDetailsStateEventType.Unauthorize:
      return unauthorize(state, event.version);
    case JeDetailsStateEventType.LayoutUpdate:
      return updateLayout(state, event.layout);
    case JeDetailsStateEventType.InitHistory:
      return initHistory(state);
    case JeDetailsStateEventType.SaveViewToHistory:
      return saveViewToHistory(state);
    case JeDetailsStateEventType.AddToHistory:
      return addToHistory(state);
    case JeDetailsStateEventType.Undo:
      return undo(state);
    case JeDetailsStateEventType.Redo:
      return redo(state);
    case JeDetailsStateEventType.CostCenterSelect:
    case JeDetailsStateEventType.ComponentSelect:
    case JeDetailsStateEventType.CostCenterItemSelect:
    case JeDetailsStateEventType.DetailsTabSelect:
    case JeDetailsStateEventType.NavigateToInvalid:
    case JeDetailsStateEventType.NavigateToInvalidTop:
    case JeDetailsStateEventType.HighlightInvalid:
    case JeDetailsStateEventType.ResetFocusedCell:
    case JeDetailsStateEventType.CostCenterSetFilter:
      return selectionReducer(state, event);
    default:
      return mainFlowReducer(state, event);
  }
}

function mainFlowReducer(state: JeDetailsState, event: JeDetailsStateEvent): JeDetailsState {
  switch (event.type) {
    case JeDetailsStateEventType.CostCenterAdd:
    case JeDetailsStateEventType.CostCenterSetField:
    case JeDetailsStateEventType.CostCenterRemove:
    case JeDetailsStateEventType.CostCenterLoad:
      return jeDetailsCcReducer(state, event);
    case JeDetailsStateEventType.TopSetField:
    case JeDetailsStateEventType.TopCommit:
    case JeDetailsStateEventType.TopReset:
    case JeDetailsStateEventType.TopRemove:
      return jeDetailsTopReducer(state, event);
    case JeDetailsStateEventType.ComponentAddSet:
    case JeDetailsStateEventType.ComponentSetField:
    case JeDetailsStateEventType.ComponentRemove:
      return jeDetailsComponentReducer(state, event);
    case JeDetailsStateEventType.CuttingListItemAdd:
    case JeDetailsStateEventType.CuttingListItemSetField:
    case JeDetailsStateEventType.CuttingListItemRemove:
      if (event.sourceType === JeElementType.Component) {
        return jeDetailsComponentReducer(state, event);
      } else {
        return jeDetailsCcItemReducer(state, event);
      }
    case JeDetailsStateEventType.UomAddSet:
      return jeDetailsUomRefReducer(state, event);
    case JeDetailsStateEventType.TopAddSet:
      return jeDetailsTopRefReducer(state, event);
    case JeDetailsStateEventType.CostCenterItemAddSet:
    case JeDetailsStateEventType.CostCenterItemSetField:
    case JeDetailsStateEventType.CostCenterItemRemove:
    case JeDetailsStateEventType.CostCenterItemChangeCc:
    case JeDetailsStateEventType.LockAll:
    case JeDetailsStateEventType.UnlockAll:
      return jeDetailsCcItemReducer(state, event);
    case JeDetailsStateEventType.OrderAdd:
    case JeDetailsStateEventType.OrderUpdate:
    case JeDetailsStateEventType.OrderRemove:
      return jeDetailsOrderReducer(state, event);
    default:
      return state;
  }
}

function updateLayout(state: JeDetailsState, layout: GridLayout): JeDetailsState {
  return setNestedEntityField(state, ['layouts', layout.name], layout);
}

function authorize(state: JeDetailsState, version: EntityVersion): JeDetailsState {
  return setEntityFields(state, {
    version,
    masterInfo: setEntityField(state.masterInfo, 'isAuthorized', true),
  });
}

function unauthorize(state: JeDetailsState, version: EntityVersion): JeDetailsState {
  return setEntityFields(state, {
    version,
    masterInfo: setEntityField(state.masterInfo, 'isAuthorized', false),
  });
}

function mergeRecalculateUpdates(state: JeDetailsState, details: JeDetails): JeDetailsState {
  const {
    jobTops, jobCostCenters, jobComponents, jobItems, jobOrders,
    contractAmount, variationsAmount, cost,
  } = details;

  return {
    ...state,
    masterInfo: setEntityFields(state.masterInfo, { cost, contractAmount, variationsAmount }),
    jobTops: jeTopTable.replaceAll(
      state.jobTops,
      queryJeTops(state).map(getJeTopId),
      jobTops,
      mergeJeTop),
    jobCostCenters: jeCcTable.mergeAll(state.jobCostCenters, jobCostCenters, mergeJeCostCenter),
    jobComponents: jeComponentTable.mergeAll(
      state.jobComponents,
      jobComponents,
      mergeJeComponent),
    // Remove all sales taxes from items table and update all items:
    // Free or component items will be updated, but sales taxes will be added into the table.
    // We have overwrite all sales taxes each time.
    jobItems: jeItemTable.mergeAll(
      // remove existing sales taxes
      jeItemTable.mergeAll(state.jobItems, jeItemTable.all(state.jobItems).filter(isJeSalesTaxItem), constant(void 0)),
      jobItems,
      // add sales taxes, but update all other items
      (oldItem, newItem, id) => oldItem && isJeSalesTaxItem(oldItem) ? oldItem : mergeJeItem(oldItem, newItem, id)),
    jobOrders: jeOrderTable.mergeAll(state.jobOrders, jobOrders, mergeJeOrder),
    isRecalculationNeeded: false,
  };
}

function mergeDetailsOnRecalculate(state: JeDetailsState, details: JeDetails): JeDetailsState {
  return mergeRecalculateUpdates(state, details);
}

function mergeDetailsOnReprice(state: JeDetailsState, details: JeDetails): JeDetailsState {
  const { jobComponents, jobItems } = details;

  // reprice operation consists of several steps:
  // 1. Merging of recalculate updates (unlike typical recalculate, reprice updates UoMs)
  const recalculatedState = mergeRecalculateUpdates(state, details);
  // 2. Merging of cost and sales tax updates
  const repricedState = setEntityFields(recalculatedState, {
    jobComponents: jeComponentTable.mergeAll(recalculatedState.jobComponents, jobComponents, repriceJeComponent),
    jobItems: jeItemTable.mergeAll(recalculatedState.jobItems, jobItems, repriceJeItem),
  });
  // 3. If repriced state has any updates comparing with recalculated state =>
  // mark affected CCs and components as dirty
  return recalculatedState !== repricedState
    ? setRecalculationNeeded(markAffectedEntitiesAsDirty(recalculatedState, repricedState))
    : repricedState;
}

function markAffectedEntitiesAsDirty(recalculated: JeDetailsState, repriced: JeDetailsState): JeDetailsState {
  const affectedCcIds = queryJeAllModifiedCostCenters(recalculated, repriced).map(getJeCostCenterId);
  const affectedComponentIds = queryJeAllModifiedComponents(recalculated, repriced).map(getJeComponentId);

  const dirty = setEntityFields(repriced, {
    jobCostCenters: jeCcTable.mergeAllById(repriced.jobCostCenters, affectedCcIds, setUpdatedDirtyType),
    jobComponents: jeComponentTable.mergeAllById(repriced.jobComponents, affectedComponentIds, setUpdatedDirtyType),
  });

  return dirty !== recalculated ? setDirty(dirty) : dirty;
}

function updateSalesTaxRates(state: JeDetailsState, rates: JeStr[]): JeDetailsState {
  // if set of sales tax rates has been changed => mark page as dirty and required recalculation
  return isEqual(orderBy(state.salesTaxRates, getJeStrId), orderBy(rates, getJeStrId))
    ? state
    : setDirty(setRecalculationNeeded(setEntityField(state, 'salesTaxRates', rates)));
}

function loadDetails(state: JeDetailsState,
                     details: JeDetails,
                     layouts?: Hash<GridLayout>,
                     hasEditPermission?: boolean): JeDetailsState {
  const {
    id, isAuthorized, isFinalized, isNotProceeding, jobDocsFolderId, jobDocsJobPlansFolderId,
    customerName, contractAmount, description, jobNumber, siteAddress, variationsAmount, cost, isDpp,
    uoms, tops, jobTops, jobCostCenters, jobComponents, jobItems, salesTaxRates, jobOrders, version,
    allowSupplierChange, memo, contactId,
  } = details;

  const componentsCcId = getJeCostCenterId(jobCostCenters[0]);
  const topRefItems = jeTopRefTable.insertAll(jeTopRefTable.create(), tops);
  const uomRefItems = jeUomRefTable.insertAll(jeUomRefTable.create(), uoms);

  return {
    ...state,
    jobId: id,
    jobComponentsCcId: componentsCcId,
    masterInfo: {
      customerName, contractAmount, description, jobNumber, siteAddress, variationsAmount, cost, isDpp, contactId,
      isAuthorized, isFinalized, isNotProceeding,
      jobDocsFolderId: state.masterInfo.jobDocsFolderId || jobDocsFolderId,
      jobDocsJobPlansFolderId: state.masterInfo.jobDocsJobPlansFolderId || jobDocsJobPlansFolderId,
    },
    uoms: uomRefItems,
    tops: topRefItems,
    jobTops: jeTopTable.insertAll(jeTopTable.create(), jobTops),
    jobCostCenters: jeCcTable.insertAll(jeCcTable.create(), jobCostCenters),
    jobComponents: jeComponentTable.insertAll(jeComponentTable.create(), jobComponents),
    jobItems: jeItemTable.insertAll(jeItemTable.create(), jobItems),
    jobOrders: jeOrderTable.insertAll(jeOrderTable.create(), jobOrders),
    salesTaxRates: salesTaxRates || [],
    selection: queryJeIsSelectionEmpty(state) ? {
      costCenterId: componentsCcId,
      componentId: jobComponents.length ? getJeComponentId(jobComponents[0]) : void 0,
      detailsTabName: getJeBestComponentDetailsTab(jobComponents[0]),
      highlightInvalidFormula: false,
    } : state.selection,
    isRecalculationNeeded: false,
    isProcessChangesAsap: false,
    isDirty: false,
    allowSupplierChange,
    hasEditPermission: hasEditPermission || state.hasEditPermission,
    layouts: layouts || state.layouts,
    memo,
    version,
  };
}

function jeDetailsUomRefReducer(state: JeDetailsState, event: JeDetailsStateEvent): JeDetailsState {
  if (event.type === JeDetailsStateEventType.UomAddSet) {
    return setEntityField(state, 'uoms', jeUomRefTable.insertAll(state.uoms, event.uomSet));
  } else {
    return state;
  }
}

function jeDetailsTopRefReducer(state: JeDetailsState, event: JeDetailsStateEvent): JeDetailsState {
  if (event.type === JeDetailsStateEventType.TopAddSet) {
    return setEntityField(state, 'tops', jeTopRefTable.insertAll(state.tops, event.topSet));
  } else {
    return state;
  }
}
